'use client';
import { useTranslation } from '@root/src/i18n/client';
import { useEffect, useState } from 'react';
import { getAllTopics, getContentsInTopics } from '@root/src/services/client/topic';
import TopicsList from './blog/TopicsList';
import ContentsList from './blog/ContentsList';
import ContentDetail from './blog/ContentDetail';

interface Topic {
  id: string | number;
  name: string;
  slug: string;
  description?: string;
  image?: string;
}

interface Content {
  id: string | number;
  title: string;
  slug: string;
  description?: string;
  image?: string[] | string;
  createdAt?: string;
  author?: string;
  content?: string;
}

type ViewState = 'topics' | 'contents' | 'detail';

export default function BlogSection() {
  const { t } = useTranslation('common');

  // State Management
  const [viewState, setViewState] = useState<ViewState>('topics');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch all topics on mount
  useEffect(() => {
    const fetchTopics = async () => {
      setLoading(true);
      try {
        const data = await getAllTopics();
        
        // Handle different response formats
        let topicsArray: Topic[] = [];
        if (data?.success && data.data) {
          topicsArray = data.data;
        } else if (Array.isArray(data)) {
          topicsArray = data;
        } else if (data?.data) {
          topicsArray = Array.isArray(data.data) ? data.data : [];
        }
        
        setTopics(topicsArray);
      } catch (error) {
        console.error('Error fetching topics:', error);
        setTopics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  // Fetch contents when topic is selected
  useEffect(() => {
    if (selectedTopic && viewState === 'contents') {
      const fetchContents = async () => {
        setLoading(true);
        try {
          const data = await getContentsInTopics(selectedTopic.slug, 1);
          
          // Handle different response formats
          let contentsArray: Content[] = [];
          if (data?.success && data.data) {
            contentsArray = data.data;
          } else if (Array.isArray(data)) {
            contentsArray = data;
          } else if (data?.data) {
            contentsArray = Array.isArray(data.data) ? data.data : [];
          }
          
          setContents(contentsArray);
          setSelectedContent(null);
        } catch (error) {
          console.error('Error fetching contents:', error);
          setContents([]);
        } finally {
          setLoading(false);
        }
      };

      fetchContents();
    }
  }, [selectedTopic, viewState]);

  // Handle topic selection
  const handleSelectTopic = (topic: Topic) => {
    setSelectedTopic(topic);
    setViewState('contents');
    setSelectedContent(null);
  };

  // Handle content selection
  const handleSelectContent = async (content: Content) => {
    setSelectedContent(content);
    setViewState('detail');
  };

  // Handle back to topics
  const handleBackToTopics = () => {
    setSelectedTopic(null);
    setSelectedContent(null);
    setViewState('topics');
    setContents([]);
  };

  // Handle back to contents
  const handleBackToContents = () => {
    setSelectedContent(null);
    setViewState('contents');
  };

  return (
    <section className="relative w-full bg-[#FBF7F0]">
      <div className="mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        {viewState === 'topics' && (
          <div className="mb-12 flex flex-col gap-2 text-center">
            <span className="text-sm uppercase tracking-[0.15em] text-[#7F6B48] font-semibold">
              {t('lytra.expBlogLabel', 'Thư viện bài viết')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#223A27]">
              {t('lytra.expBlogTitle', 'Khám phá các chuyên đề')}
            </h2>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#223A27]"></div>
            </div>
            <p className="mt-4 text-[#666]">{t('common.loading', 'Đang tải...')}</p>
          </div>
        )}

        {/* Views */}
        {!loading && (
          <>
            {/* Topics View */}
            {viewState === 'topics' && (
              <TopicsList topics={topics} onSelectTopic={handleSelectTopic} />
            )}

            {/* Contents View */}
            {viewState === 'contents' && selectedTopic && (
              <ContentsList
                topic={selectedTopic}
                contents={contents}
                onSelectContent={handleSelectContent}
                onBack={handleBackToTopics}
              />
            )}

            {/* Content Detail View */}
            {viewState === 'detail' && selectedTopic && selectedContent && (
              <ContentDetail
                topic={selectedTopic}
                content={selectedContent}
                relatedContents={contents}
                onSelectContent={handleSelectContent}
                onBack={handleBackToContents}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}
